/**
 * Bulk-import dialog for the Tag Library. Accepts CSV either as a pasted
 * string or an uploaded .csv file. Three columns:
 *   Name (required), Description (optional), Aliases (optional, pipe-separated)
 * The first row is treated as a header if its first cell case-insensitively
 * matches "name" or "tag". Otherwise every row is data.
 *
 * Duplicates against the existing library and within the file itself are
 * skipped on the server; this UI only validates row-level shape and shows
 * a preview before calling tags.bulkImport.
 */
import { useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/lib/toast";
import { trpc } from "@/lib/trpc";
import { Upload, FileText, AlertCircle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ParsedTagRow {
  name: string;
  description?: string;
  aliases?: string[];
}

interface ParsedFile {
  rows: ParsedTagRow[];
  errors: Array<{ line: number; message: string }>;
}

const HEADER_HINT_NAMES = new Set(["name", "tag", "tag name", "activity"]);

/** Split a single CSV line on commas, honouring "double-quoted" cells. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      out.push(cell);
      cell = "";
    } else {
      cell += ch;
    }
  }
  out.push(cell);
  return out.map((c) => c.trim());
}

function parseCsv(raw: string): ParsedFile {
  const lines = raw
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((l) => l)
    .filter((_, idx, arr) => idx < arr.length - 1 || arr[idx].trim().length > 0);

  const rows: ParsedTagRow[] = [];
  const errors: Array<{ line: number; message: string }> = [];
  if (lines.length === 0) return { rows, errors };

  const first = splitCsvLine(lines[0]);
  const hasHeader = first.length > 0 && HEADER_HINT_NAMES.has(first[0].toLowerCase());
  const startIdx = hasHeader ? 1 : 0;

  for (let i = startIdx; i < lines.length; i++) {
    const lineNumber = i + 1;
    const line = lines[i];
    if (!line.trim()) continue;
    const cells = splitCsvLine(line);
    const name = (cells[0] ?? "").trim();
    if (!name) {
      errors.push({ line: lineNumber, message: "Missing tag name" });
      continue;
    }
    if (name.length > 255) {
      errors.push({ line: lineNumber, message: `Name too long (${name.length} chars)` });
      continue;
    }
    const description = (cells[1] ?? "").trim() || undefined;
    if (description && description.length > 500) {
      errors.push({ line: lineNumber, message: `Description too long (${description.length} chars)` });
      continue;
    }
    const aliasesRaw = (cells[2] ?? "").trim();
    const aliases = aliasesRaw
      ? aliasesRaw
          .split("|")
          .map((a) => a.trim())
          .filter(Boolean)
      : undefined;

    rows.push({ name, description, aliases });
  }

  return { rows, errors };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: () => void;
  /** Which tag library this dialog imports into. Defaults to "activity" for backwards compatibility. */
  library?: "activity" | "topic";
}

export function TagCsvImportDialog({ open, onOpenChange, onImported, library = "activity" }: Props) {
  const [raw, setRaw] = useState("");
  const [filename, setFilename] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parsed = useMemo<ParsedFile>(() => parseCsv(raw), [raw]);

  const bulkImportApi = library === "activity" ? trpc.tags.bulkImport : trpc.topicTags.bulkImport;
  const importMutation = bulkImportApi.useMutation({
    onSuccess: (summary) => {
      const parts: string[] = [];
      parts.push(`Imported ${summary.tagsCreated} new tag${summary.tagsCreated === 1 ? "" : "s"}`);
      if (summary.tagsSkipped > 0) parts.push(`skipped ${summary.tagsSkipped} duplicate${summary.tagsSkipped === 1 ? "" : "s"}`);
      if (summary.aliasesCreated > 0) parts.push(`${summary.aliasesCreated} alias${summary.aliasesCreated === 1 ? "" : "es"} added`);
      if (summary.aliasesSkipped > 0) parts.push(`${summary.aliasesSkipped} alias${summary.aliasesSkipped === 1 ? "" : "es"} skipped`);
      toast.success(parts.join(", "));
      if (summary.aliasesOrphaned.length > 0) {
        toast.warning(
          `${summary.aliasesOrphaned.length} alias${summary.aliasesOrphaned.length === 1 ? "" : "es"} pointed at unknown tags and were dropped`,
        );
      }
      reset();
      onImported();
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message ?? "Import failed"),
  });

  function reset() {
    setRaw("");
    setFilename(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function onFileChosen(file: File) {
    if (!/\.(csv|tsv|txt)$/i.test(file.name)) {
      toast.error("Please choose a .csv, .tsv, or .txt file");
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      toast.error("File is larger than 1 MB — paste in batches instead");
      return;
    }
    const text = await file.text();
    setRaw(text);
    setFilename(file.name);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) onFileChosen(file);
  }

  const canImport = parsed.rows.length > 0 && !importMutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import Tags from CSV</DialogTitle>
          <DialogDescription>
            Three columns: <span className="font-mono">Name, Description, Aliases</span>.
            Description and aliases are optional. Aliases are pipe-separated
            (<span className="font-mono">sts | stand-up</span>). Duplicate names and aliases are skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Drop zone / file picker */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="border border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,.txt,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onFileChosen(file);
              }}
            />
            <Upload className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Drop a CSV file here, or{" "}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-primary underline-offset-2 hover:underline"
              >
                browse
              </button>
            </p>
            {filename && (
              <p className="text-xs text-muted-foreground mt-1">
                <FileText className="inline w-3 h-3 mr-1" />
                {filename}
              </p>
            )}
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">or paste</span>
            </div>
          </div>

          <div>
            <textarea
              value={raw}
              onChange={(e) => {
                setRaw(e.target.value);
                if (filename) setFilename(null);
              }}
              placeholder={`Name,Description,Aliases\nStanding balance,Static balance in upright posture,\nSit-to-stand,Lower-extremity transfer drill,sts|stand-up`}
              rows={6}
              className="w-full text-sm font-mono border border-border rounded-md p-3 bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
          </div>

          {/* Preview */}
          {raw.trim() && (
            <div className="border border-border rounded-md overflow-hidden">
              <div className="px-3 py-2 bg-muted/30 border-b border-border flex items-center justify-between text-xs">
                <span className="font-medium">
                  Preview — {parsed.rows.length} row{parsed.rows.length === 1 ? "" : "s"}
                </span>
                {parsed.errors.length > 0 && (
                  <span className="text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {parsed.errors.length} error{parsed.errors.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <div className="max-h-48 overflow-y-auto text-xs">
                {parsed.errors.length > 0 && (
                  <ul className="px-3 py-2 space-y-0.5 bg-destructive/5 border-b border-border">
                    {parsed.errors.slice(0, 5).map((err) => (
                      <li key={err.line} className="text-destructive">
                        Line {err.line}: {err.message}
                      </li>
                    ))}
                    {parsed.errors.length > 5 && (
                      <li className="text-muted-foreground">
                        +{parsed.errors.length - 5} more…
                      </li>
                    )}
                  </ul>
                )}
                {parsed.rows.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-muted/20 sticky top-0">
                      <tr className="text-left">
                        <th className="px-3 py-1.5 font-medium">Name</th>
                        <th className="px-3 py-1.5 font-medium">Description</th>
                        <th className="px-3 py-1.5 font-medium">Aliases</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.rows.slice(0, 50).map((row, idx) => (
                        <tr key={idx} className={cn("border-t border-border", idx % 2 === 1 && "bg-muted/10")}>
                          <td className="px-3 py-1.5 font-mono">{row.name}</td>
                          <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[260px]">
                            {row.description ?? "—"}
                          </td>
                          <td className="px-3 py-1.5 text-muted-foreground">
                            {row.aliases?.join(", ") ?? "—"}
                          </td>
                        </tr>
                      ))}
                      {parsed.rows.length > 50 && (
                        <tr>
                          <td colSpan={3} className="px-3 py-1.5 text-center text-muted-foreground">
                            +{parsed.rows.length - 50} more rows…
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                ) : (
                  parsed.errors.length === 0 && (
                    <p className="px-3 py-3 text-muted-foreground">No rows parsed yet.</p>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canImport}
            onClick={() => importMutation.mutate({ rows: parsed.rows })}
          >
            {importMutation.isPending ? (
              "Importing…"
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                Import {parsed.rows.length} tag{parsed.rows.length === 1 ? "" : "s"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
